import { useState, useEffect, useCallback } from "react";
import { useLang, ui, tx } from "../i18n";
import { Icon } from "./Icon";

const S = {
  title: { es: "Panel de Administración", en: "Admin Panel" },
  subtitle: { es: "Mensajes recibidos y estadísticas de uso (solo el equipo de preparatepr.org)", en: "Received messages and usage stats (preparatepr.org team only)" },
  password: { es: "Contraseña de administrador", en: "Admin password" },
  login: { es: "Entrar", en: "Log in" },
  wrong: { es: "Contraseña incorrecta o sesión expirada.", en: "Wrong password or expired session." },
  locked: { es: "Demasiados intentos. Espere 15 minutos.", en: "Too many attempts. Wait 15 minutes." },
  statsTitle: { es: "Secciones más visitadas (anónimo)", en: "Most visited sections (anonymous)" },
  visits: { es: "visitas", en: "visits" },
  total: { es: "Total de visitas registradas", en: "Total visits recorded" },
  msgsTitle: { es: "Bandeja de Mensajes", en: "Message Inbox" },
  noMsgs: { es: "Aún no hay mensajes.", en: "No messages yet." },
  noStats: { es: "Aún no hay visitas registradas.", en: "No visits recorded yet." },
  refresh: { es: "Actualizar", en: "Refresh" },
  logout: { es: "Salir", en: "Log out" },
  from: { es: "De", en: "From" },
};

const t = (o, lang) => o[lang] ?? o.es;
const API = process.env.REACT_APP_BACKEND_URL;

export function AdminPanel() {
  const { lang } = useLang();
  const [token, setToken] = useState(() => localStorage.getItem("ppr-admin-token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msgFilter, setMsgFilter] = useState("pending");

  const loadData = useCallback(async (tk) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${tk}` };
      const [rs, rm] = await Promise.all([
        fetch(`${API}/api/admin/stats`, { headers }),
        fetch(`${API}/api/admin/messages`, { headers }),
      ]);
      if (rs.status === 401 || rm.status === 401) throw new Error("401");
      setStats(await rs.json());
      setMessages((await rm.json()).messages || []);
    } catch {
      setToken("");
      localStorage.removeItem("ppr-admin-token");
      setError(t(S.wrong, lang));
    }
    setLoading(false);
  }, [lang]);

  useEffect(() => {
    if (token) loadData(token);
  }, [token, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (r.status === 429) { setError(t(S.locked, lang)); return; }
      if (!r.ok) { setError(t(S.wrong, lang)); return; }
      const { token: tk } = await r.json();
      localStorage.setItem("ppr-admin-token", tk);
      setToken(tk);
    } catch {
      setError(t(S.wrong, lang));
    }
  };

  const logout = () => {
    localStorage.removeItem("ppr-admin-token");
    setToken("");
    setStats(null);
    setMessages(null);
  };

  const msgAction = async (id, mode) => {
    try {
      const r = await fetch(`${API}/api/admin/messages/${id}`, {
        method: mode === "delete" ? "DELETE" : "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        ...(mode !== "delete" ? { body: JSON.stringify({ action: mode }) } : {}),
      });
      if (r.ok) await loadData(token);
    } catch {}
  };

  const replyHref = (m) => {
    const subject = lang === "es" ? "Re: Tu mensaje a preparatepr.org" : "Re: Your message to preparatepr.org";
    const body = (lang === "es" ? `Hola ${m.name},\n\n` : `Hi ${m.name},\n\n`) +
      "\n\n" + (lang === "es" ? "— Tu mensaje:\n" : "— Your message:\n") + m.message;
    return `mailto:${m.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [];
    lines.push(lang === "es" ? "SECCION,VISITAS" : "SECTION,VISITS");
    for (const [tab, count] of Object.entries(stats?.counts || {})) lines.push(`${esc(tab)},${count}`);
    lines.push("");
    lines.push(lang === "es" ? "FECHA,NOMBRE,CORREO,MUNICIPIO,ARCHIVADO,MENSAJE" : "DATE,NAME,EMAIL,MUNICIPALITY,ARCHIVED,MESSAGE");
    for (const m of messages || []) {
      lines.push([m.createdAt, m.name, m.email, m.municipality, m.archived ? "sí" : "no", m.message].map(esc).join(","));
    }
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `preparatepr-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!token) {
    return (
      <div data-testid="admin-login" className="px-4 sm:px-8 lg:px-12 py-12 max-w-md">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{t(S.title, lang)}</h2>
        <p className="mt-1 mb-6 text-sm text-slate-600">{t(S.subtitle, lang)}</p>
        <form onSubmit={login} className="rounded-lg border border-slate-300 bg-white p-6">
          <label className="block">
            <span className="mb-1.5 block text-sm font-bold text-slate-600">{t(S.password, lang)}</span>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border-2 border-slate-300 px-4 py-2.5 text-base focus:border-amber-600 focus:outline-none"
            />
          </label>
          {error && <p data-testid="admin-error" className="mt-2 text-sm font-semibold text-red-700">{error}</p>}
          <button
            data-testid="admin-login-btn"
            type="submit"
            className="mt-4 w-full rounded-md bg-[#1C2B4A] px-5 py-3 text-base font-bold text-white hover:bg-amber-700 transition-colors duration-200 active:scale-[0.98]"
          >
            {t(S.login, lang)}
          </button>
        </form>
      </div>
    );
  }

  const entries = stats ? Object.entries(stats.counts || {}).sort((a, b) => b[1] - a[1]) : [];
  const max = entries.length ? entries[0][1] : 1;

  return (
    <div data-testid="admin-panel" className="px-4 sm:px-8 lg:px-12 py-8 sm:py-10 max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{t(S.title, lang)}</h2>
        <div className="flex gap-2">
          <button data-testid="admin-export-btn" onClick={exportCsv}
            className="inline-flex items-center gap-2 rounded-md border-2 border-green-700 px-4 py-2 text-sm font-bold text-green-800 hover:bg-green-700 hover:text-white transition-colors duration-200 active:scale-[0.98]">
            <Icon name="Printer" className="w-4 h-4" /> CSV
          </button>
          <button data-testid="admin-refresh-btn" onClick={() => loadData(token)} disabled={loading}
            className="inline-flex items-center gap-2 rounded-md border-2 border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:border-slate-500 transition-colors duration-200 disabled:opacity-50">
            <Icon name="RefreshCw" className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> {t(S.refresh, lang)}
          </button>
          <button data-testid="admin-logout-btn" onClick={logout}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 transition-colors duration-200">
            {t(S.logout, lang)}
          </button>
        </div>
      </div>

      <section className="rounded-lg border border-slate-300 bg-white p-6 mb-8">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4">{t(S.statsTitle, lang)}</h3>
        {stats && (
          <p data-testid="stats-total" className="mb-4 font-mono-pr text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            {t(S.total, lang)}: {stats.total}
          </p>
        )}
        {entries.length === 0 && <p className="text-sm text-slate-500">{t(S.noStats, lang)}</p>}
        <div className="space-y-2.5">
          {entries.map(([tab, count]) => (
            <div key={tab} data-testid={`stats-row-${tab}`} className="flex items-center gap-3">
              <span className="w-40 shrink-0 truncate text-sm font-semibold text-slate-700">{tx(ui.tabs[tab] || { es: tab }, lang)}</span>
              <div className="h-6 flex-1 rounded bg-slate-100 overflow-hidden">
                <div className="h-full rounded bg-amber-600" style={{ width: `${Math.max(4, (count / max) * 100)}%` }} />
              </div>
              <span className="w-16 shrink-0 text-right font-mono-pr text-xs font-bold text-slate-600">{count} {t(S.visits, lang)}</span>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4">{t(S.msgsTitle, lang)} ({messages?.length ?? 0})</h3>
        <div data-testid="inbox-filter-tabs" className="mb-4 flex gap-1 rounded-lg border border-slate-300 bg-slate-100 p-1 w-fit">
          {[["pending", lang === "es" ? "Pendientes" : "Pending"], ["archived", lang === "es" ? "Archivados" : "Archived"], ["all", lang === "es" ? "Todos" : "All"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`inbox-filter-${id}`}
              onClick={() => setMsgFilter(id)}
              className={`rounded-md px-4 py-2 text-sm font-bold transition-colors duration-150 ${
                msgFilter === id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {label} ({(messages || []).filter((m) => id === "all" ? true : id === "archived" ? m.archived : !m.archived).length})
            </button>
          ))}
        </div>
        {messages && messages.length === 0 && <p className="text-sm text-slate-500">{t(S.noMsgs, lang)}</p>}
        <div className="space-y-3">
          {(messages || [])
            .filter((m) => (msgFilter === "all" ? true : msgFilter === "archived" ? m.archived : !m.archived))
            .map((m, i) => (
            <div key={m.id || i} data-testid={`admin-message-${i}`} className={`rounded-lg border bg-white p-5 ${m.archived ? "border-slate-200 opacity-70" : "border-slate-300"}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="font-bold text-slate-900">
                  {m.name} {m.municipality ? <span className="font-normal text-slate-500">· {m.municipality}</span> : null}
                  {m.archived && (
                    <span className="ml-2 rounded-full bg-slate-200 px-2.5 py-0.5 text-[11px] font-bold text-slate-500">
                      {lang === "es" ? "Archivado" : "Archived"}
                    </span>
                  )}
                </p>
                <span className="font-mono-pr text-xs text-slate-400">
                  {m.createdAt ? new Date(m.createdAt).toLocaleString(lang === "es" ? "es-PR" : "en-US") : ""}
                </span>
              </div>
              <a href={`mailto:${m.email}`} className="text-sm font-semibold text-amber-700 hover:text-amber-900">{m.email}</a>
              <p className="mt-2 text-sm leading-relaxed text-slate-700 whitespace-pre-line">{m.message}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                <a
                  data-testid={`admin-reply-${i}`}
                  href={replyHref(m)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#1C2B4A] px-3.5 py-2 text-xs font-bold text-white hover:bg-amber-700 transition-colors duration-150 active:scale-[0.97]"
                >
                  <Icon name="MessageCircle" className="w-3.5 h-3.5" />
                  {lang === "es" ? "Responder" : "Reply"}
                </a>
                <button
                  data-testid={`admin-archive-${i}`}
                  onClick={() => msgAction(m.id, m.archived ? "unarchive" : "archive")}
                  className="inline-flex items-center gap-1.5 rounded-md border-2 border-slate-300 px-3.5 py-1.5 text-xs font-bold text-slate-600 hover:border-slate-500 transition-colors duration-150 active:scale-[0.97]"
                >
                  <Icon name="RotateCcw" className="w-3.5 h-3.5" />
                  {m.archived ? (lang === "es" ? "Restaurar" : "Restore") : (lang === "es" ? "Archivar" : "Archive")}
                </button>
                <button
                  data-testid={`admin-delete-${i}`}
                  onClick={() => { if (window.confirm(lang === "es" ? "¿Eliminar este mensaje permanentemente?" : "Permanently delete this message?")) msgAction(m.id, "delete"); }}
                  className="inline-flex items-center gap-1.5 rounded-md border-2 border-red-200 px-3.5 py-1.5 text-xs font-bold text-red-600 hover:border-red-500 hover:bg-red-50 transition-colors duration-150 active:scale-[0.97]"
                >
                  {lang === "es" ? "Eliminar" : "Delete"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
